import { MouseEvent, useEffect, useRef, useState } from 'react';
import './index.less';
import cN from 'classnames';
import { useConfigProvider } from '@/lib/context';

const generateId = (text) => {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-');
};

enum TableOfContentPosition {
  STICKY = 'sticky',
  BOTTOM = 'bottom',
}

interface IProps {
  containerClassName: string;
  offsetTop?: number;
  offsetBottom?: number;
  className?: string;
}

const TableOfContents = ({ containerClassName, offsetTop = 0, offsetBottom = 0, className }: IProps) => {
  const [headings, setHeadings] = useState([]);
  const [activeId, setActiveId] = useState('');
  const [position, setPosition] = useState<TableOfContentPosition>(null);
  const { isMobile } = useConfigProvider();

  useEffect(() => {
    const headingElements = Array.from(
      document.querySelectorAll(containerClassName + ' h2, ' + containerClassName + ' h3'),
    );

    headingElements.forEach((heading) => {
      if (!heading.id) {
        heading.id = generateId(heading.textContent);
      }
    });

    setHeadings(
      headingElements.map((heading) => ({
        id: heading.id,
        text: heading.textContent,
        level: heading.tagName,
      })),
    );

    if (isMobile) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      {
        rootMargin: '0px 0px -75% 0px',
      },
    );

    headingElements.forEach((heading) => {
      observer.observe(heading);
    });

    return () => {
      if (!isMobile) observer.disconnect();
    };
  }, []);

  const scrollToHeading = (id: string) => {
    const element = document.getElementById(id);
    const yOffset = -80;
    const y = element.getBoundingClientRect().top + document.documentElement.scrollTop + yOffset;

    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  const tocRef = useRef(null);
  const lastScrollTopRef = useRef(0);

  useEffect(() => {
    if (isMobile || headings.length === 0) return;
    const tocElement = tocRef.current;
    const parentNode = tocElement.parentNode;

    const topDiv = document.createElement('div');
    const bottomDiv = document.createElement('div');

    topDiv.style.position = 'absolute';
    topDiv.style.top = offsetTop + 'px';
    topDiv.style.width = '100%';
    topDiv.style.height = '1px';

    bottomDiv.style.position = 'absolute';
    bottomDiv.style.bottom = -(document.documentElement.clientHeight - tocElement.offsetHeight) + offsetBottom + 'px';
    bottomDiv.style.width = '100%';
    bottomDiv.style.height = '1px';

    parentNode.insertBefore(topDiv, parentNode.firstChild);
    parentNode.appendChild(bottomDiv);

    const tocObserver = new IntersectionObserver(
      ([entry]) => {
        const scrollTop = document.documentElement.scrollTop;
        if (scrollTop > lastScrollTopRef.current) {
          setPosition(entry.isIntersecting ? null : TableOfContentPosition.STICKY);
        } else {
          setPosition(null);
        }
        lastScrollTopRef.current = scrollTop;
      },
      {
        threshold: 0,
      },
    );

    const bottomObserver = new IntersectionObserver(
      ([entry]) => {
        const scrollTop = document.documentElement.scrollTop;
        if (scrollTop > lastScrollTopRef.current) {
          setPosition(TableOfContentPosition.BOTTOM);
        } else if (!entry.isIntersecting && lastScrollTopRef.current !== 0) {
          setPosition(TableOfContentPosition.STICKY);
        }
        lastScrollTopRef.current = scrollTop;
      },
      {
        threshold: 0,
      },
    );

    if (tocElement) {
      tocObserver.observe(topDiv);
      bottomObserver.observe(bottomDiv);
    }

    return () => {
      tocObserver.disconnect();
      bottomObserver.disconnect();
      parentNode.removeChild(topDiv);
      parentNode.removeChild(bottomDiv);
    };
  }, [tocRef, headings.length]);

  const [isExpand, setIsExpand] = useState(false);
  const handleClick = () => {
    if (isExpand === false) setIsExpand(true);
  };
  const handleArrowClick = () => {
    setIsExpand(!isExpand);
  };

  return (
    <nav
      className={cN('table-of-contents', position, isExpand && 'expand', className)}
      ref={tocRef}
      onClick={handleClick}
    >
      <div className="title">TABLE OF CONTENTS</div>
      <div className="arrow" onClick={handleArrowClick}>
        <img
          width={12}
          height={12}
          src="https://static.pietrastudio.com/public/file_uploads/63a35e350ff350d44fa2eede0b68de36.svg"
          alt="arrow"
        />
      </div>
      <ul>
        {headings.map((heading) => (
          <li
            key={heading.id}
            className={`table-of-contents-item table-of-contents-${heading.level.toLowerCase()} ${
              heading.id === activeId ? 'active' : ''
            }`}
          >
            <a
              onClick={(e: MouseEvent) => {
                e.preventDefault();
                scrollToHeading(heading.id);
              }}
              href={`#${heading.id}`}
            >
              {heading.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default TableOfContents;
